/**
 * i18n Configuration
 * Настройка интернационализации для CRM
 */
import { createI18n } from 'vue-i18n'
import { messages, type Locale, type MessageSchema } from './locales'

// Язык из localStorage, по умолчанию русский (DEV-2490): менеджер, открывший
// ссылку из бота в чистом браузере, видел форму входа по-английски. Русский —
// язык по умолчанию и у бэкенда (user-locale). Вьетнамский среди языков браузера —
// вьетнамский: сотрудник, который не выбирал язык, иначе получил бы русский.
const getStoredLocale = (): Locale => {
  const stored = localStorage.getItem('crm-locale')
  if (stored && (stored === 'ru' || stored === 'en' || stored === 'vn')) {
    return stored as Locale
  }
  const languages = navigator.languages?.length ? navigator.languages : [navigator.language]
  return languages.some((lang) => lang?.toLowerCase().startsWith('vi')) ? 'vn' : 'ru'
}

// Сохранить выбранный язык в localStorage
export const setStoredLocale = (locale: Locale): void => {
  localStorage.setItem('crm-locale', locale)
}

/**
 * Русская плюрализация (3 формы: «1 задача / 2 задачи / 5 задач»).
 * Порядок форм в сообщении — `one | few | many`, напр. `'задача | задачи | задач'`.
 * Число для выбора формы vue-i18n берёт из именованных `n`/`count` или из явного
 * plural-аргумента. `Math.min` подстраховывает сообщения с меньшим числом форм.
 * en/vn используют встроенное правило intlify (английское one/other и одну форму
 * соответственно — для них этого достаточно).
 */
const russianPluralization = (choice: number, choicesLength: number): number => {
  const n = Math.abs(choice)
  const mod10 = n % 10
  const mod100 = n % 100
  let index: number
  if (mod10 === 1 && mod100 !== 11) {
    index = 0 // одна задача
  } else if (mod10 >= 2 && mod10 <= 4 && !(mod100 >= 12 && mod100 <= 14)) {
    index = 1 // две задачи
  } else {
    index = 2 // пять задач
  }
  return Math.min(index, choicesLength - 1)
}

export const i18n = createI18n<[MessageSchema], Locale>({
  legacy: false, // Использовать Composition API
  locale: getStoredLocale(), // Язык по умолчанию
  fallbackLocale: 'en', // Fallback язык
  messages,
  pluralRules: { ru: russianPluralization }, // корректные русские формы (см. выше)
  globalInjection: true, // Глобальная инъекция $t
})

export { type Locale, availableLocales } from './locales'
